import {
  Check,
  ChevronLeft,
  Copy,
  Download,
  ExternalLink,
  FileDown,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { formatBirthDate, formatFileSize } from '@virtual-care/isomorphic';
import { DownloadFile } from '@virtual-care/media-web';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@virtual-care/shadcn';
import { Loading, ReservationStatus, useData } from '@virtual-care/ui';

import { Header, ProfileCard } from '../../components/header/index.ts';

function CopyableField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm">{label}</p>
      <div className="bg-muted flex items-center gap-2 rounded-md border px-3 py-2">
        <span className="flex-1 font-mono text-sm break-all">{value}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="shrink-0"
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

export default function PrintingRequest() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: record, isLoading } = useData(
    'GET /printing-requests/{id}',
    { id: id! },
    { enabled: !!id },
  );

  if (isLoading) return <Loading />;
  if (!record) return null;

  const doctorName = record.doctor
    ? `${record.doctor.firstName} ${record.doctor.lastName}`
    : undefined;
  const isFinished = record.status === 'FINISHED';

  return (
    <>
      <Header>
        <ProfileCard />
      </Header>
      <div className="min-h-screen px-4">
        <div className="mx-auto max-w-3xl">
          <button
            onClick={() => navigate('/printing')}
            className="text-muted-foreground mb-4 flex items-center gap-1 text-sm hover:underline"
          >
            <ChevronLeft className="h-4 w-4 ltr:rotate-180" />
            <span>{t('common.back')}</span>
          </button>

          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('printing.cards.prefix')} {record.counter}#
            </h1>
            <ReservationStatus.Root status={record.status}>
              <ReservationStatus.Indicator />
              <ReservationStatus.Label
                render={(meta) => t(meta.key)}
              />
            </ReservationStatus.Root>
          </div>

          {isFinished && record.downloadUrl && record.downloadPassword && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{t('printing.detail.linkReady')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <CopyableField
                  label={t('printing.detail.downloadUrl')}
                  value={record.downloadUrl}
                />
                <CopyableField
                  label={t('printing.detail.downloadPassword')}
                  value={record.downloadPassword}
                />
                <Button asChild className="w-full">
                  <a
                    href={record.downloadUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t('printing.detail.openLink')}
                  </a>
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>{t('printing.detail.requestDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                <span className="text-muted-foreground">
                  {t('printing.detail.description')}:
                </span>{' '}
                {record.description}
              </p>
              <p>
                <span className="text-muted-foreground">
                  {t('printing.detail.submittedOn')}:
                </span>{' '}
                {formatBirthDate(record.createdAt)}
              </p>
              {doctorName && (
                <p>
                  <span className="text-muted-foreground">
                    {t('printing.cards.doctorName')}:
                  </span>{' '}
                  {doctorName}
                </p>
              )}
              {record.files.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-2">
                    {t('printing.detail.files')}:
                  </p>
                  <div className="space-y-2">
                    {record.files.map((entry) => (
                      <div
                        key={entry.id}
                        className="bg-background flex flex-col gap-2 rounded border p-2 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FileDown className="text-muted-foreground h-4 w-4 shrink-0" />
                          <span className="truncate text-sm font-medium">
                            {entry.file.name}
                          </span>
                          <span className="text-muted-foreground shrink-0 text-xs">
                            ({entry.file.contentType})
                          </span>
                          {entry.file.size > 0 && (
                            <span className="text-muted-foreground shrink-0 text-xs">
                              {formatFileSize(entry.file.size)}
                            </span>
                          )}
                        </div>
                        <DownloadFile
                          fileId={entry.fileId}
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto"
                          icon={<Download className="h-4 w-4" />}
                        >
                          {t('printing.detail.downloadFile')}
                        </DownloadFile>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
